import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../Icon';
import { trackEvent } from '../../utils/analytics';
import { PHONE } from '../../content/contacts';
import { tie } from './typography';

/**
 * Короткий заклик посеред довгої сторінки.
 *
 * На сторінці послуги єдина кнопка заявки стояла в самому кінці — на
 * телефоні до неї понад дванадцять екранів. Людина, яка вже на третьому
 * розділі зрозуміла, що їй це потрібно, мусила або гортати до кінця, або
 * шукати меню. Цей блок ставиться між розділами, там, де думка завершена.
 *
 * Він навмисно світлий: градієнтний банер у кінці сторінки лишається
 * головним закликом, а цей — лише нагадування, що звернутися можна вже
 * зараз. Два однакові банери поспіль читалися б як реклама.
 *
 * `source` потрапляє в статистику: за ним видно, з якого місця сторінки
 * люди йдуть на /kontakty.
 *
 * Заголовок — абзац, а не h2: це не розділ сторінки, і в змісті для
 * екранного диктора йому не місце між справжніми розділами. Якщо блок
 * стоїть окремою секцією, передайте titleAs="h2".
 */
const InlineCta = ({
  title = 'Уже бачите, що це ваш випадок?',
  text = 'Не обов’язково дочитувати до кінця: досить імені й телефону — решту з’ясуємо в розмові.',
  source = 'inline-cta',
  buttonLabel = 'Отримати консультацію',
  phone = true,
  icon = 'fa-comment-dots',
  titleAs: Title = 'p',
  spacing = 'mt-14 lg:mt-20 first:mt-0',
  className = ''
}) => (
  <div
    className={`${spacing} rounded-2xl border border-teal-100 bg-teal-50/50 p-5 sm:p-6 lg:p-8 flex flex-col xl:flex-row xl:items-center gap-5 xl:gap-10 ${className}`}
  >
    <div className="flex items-start gap-4 flex-1 min-w-0">
      <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-white border border-teal-100 text-teal-700 flex items-center justify-center" aria-hidden="true">
        <Icon name={icon} className="text-lg" />
      </span>
      <div className="min-w-0">
        <Title className="text-lg lg:text-xl font-bold font-display text-slate-900 leading-snug text-balance">{tie(title)}</Title>
        {text && <p className="mt-1.5 text-slate-600 leading-relaxed max-w-2xl">{tie(text)}</p>}
      </div>
    </div>

    {/* На телефоні кнопки на всю ширину одна під одною: так у них легко
        влучити пальцем, і номер не рветься посередині. */}
    <div className="flex flex-col sm:flex-row gap-3 xl:flex-shrink-0">
      <Link
        to="/kontakty"
        onClick={() => trackEvent('cta_click', source)}
        className="inline-flex items-center justify-center gap-3 rounded-lg bg-teal-700 px-6 py-3 font-bold text-white hover:bg-teal-800 transition-colors duration-200 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2"
      >
        {buttonLabel}
        <Icon name="fa-arrow-right" />
      </Link>
      {phone && (
        <a
          href={PHONE.href}
          onClick={() => trackEvent('contact_channel', `${source}-phone`)}
          className="inline-flex items-center justify-center gap-3 rounded-lg border border-slate-300 bg-white px-6 py-3 font-bold text-slate-800 hover:border-teal-600 hover:text-teal-800 transition-colors duration-200 whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2"
        >
          <Icon name="fa-phone" className="text-teal-700" />
          {PHONE.label}
        </a>
      )}
    </div>
  </div>
);

export default InlineCta;
